import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { getRetryDelaySeconds } from "@ease/shared";
import type {
  CollabMessage,
  CollabUser,
  ConflictNoticePayload,
  CursorPosition,
  EditDiffPayload,
  PageCollabDiff,
  PageCollabSessionBridge,
  UserJoinedPayload,
  UserLeftPayload,
} from "@ease/page-builder/collaboration";
import { detectConflict } from "@ease/page-builder/collaboration";
import type { PuckDiffOp } from "@ease/page-builder";
import { apiFetch } from "@/lib/api-client";

interface RemoteDiffEvent {
  payload: EditDiffPayload;
  userId: string;
  pageId: string;
  timestamp: number;
}

interface RemoteCommentEvent {
  eventType: "added" | "resolved";
  payload: {
    commentId: string;
    componentId: string | null;
    componentType: string | null;
    authorName?: string;
    resolvedBy?: string | null;
  };
  userId: string;
  pageId: string;
  timestamp: number;
}

interface UsePageCollabOptions {
  onRemoteDiff?: (event: RemoteDiffEvent) => void;
  onConflict?: (payload: ConflictNoticePayload) => void;
  onCommentEvent?: (event: RemoteCommentEvent) => void;
}

interface UsePageCollabResult {
  collaborators: CollabUser[];
  currentUser: PageCollabSessionBridge["currentUser"] | null;
  isConnected: boolean;
  connectError: string | null;
  sendCursor: (cursor: CursorPosition) => void;
  sendDiff: (payload: EditDiffPayload) => void;
  sendCommentEvent: (payload: RemoteCommentEvent["payload"] & { eventType: RemoteCommentEvent["eventType"] }) => void;
}

const MAX_RECONNECT_ATTEMPTS = 8;
const PENDING_DIFF_TTL_MS = 5_000;
const CURSOR_THROTTLE_MS = 100;

function isPuckDiffOp(diff: PageCollabDiff): diff is PuckDiffOp {
  return typeof diff === "object" && diff !== null && "action" in diff;
}

export function usePageCollab(
  pageId: string | undefined,
  options?: UsePageCollabOptions,
): UsePageCollabResult {
  const [collaborators, setCollaborators] = useState<CollabUser[]>([]);
  const [currentUser, setCurrentUser] = useState<PageCollabSessionBridge["currentUser"] | null>(null);
  const [isConnected, setIsConnected] = useState(false);
  const [connectError, setConnectError] = useState<string | null>(null);
  const wsRef = useRef<WebSocket | null>(null);
  const reconnectAttempts = useRef(0);
  const reconnectTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const shouldReconnectRef = useRef(true);
  const pendingDiffsRef = useRef<Array<{ payload: EditDiffPayload; createdAt: number }>>([]);
  const pendingCursorRef = useRef<CursorPosition | null>(null);
  const cursorFlushTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const lastCursorSentAtRef = useRef(0);
  const onRemoteDiffRef = useRef(options?.onRemoteDiff);
  const onConflictRef = useRef(options?.onConflict);
  const onCommentEventRef = useRef(options?.onCommentEvent);

  useEffect(() => {
    onRemoteDiffRef.current = options?.onRemoteDiff;
    onConflictRef.current = options?.onConflict;
    onCommentEventRef.current = options?.onCommentEvent;
  }, [options?.onCommentEvent, options?.onConflict, options?.onRemoteDiff]);

  const prunePendingDiffs = useCallback(() => {
    const cutoff = Date.now() - PENDING_DIFF_TTL_MS;
    pendingDiffsRef.current = pendingDiffsRef.current.filter((entry) => entry.createdAt >= cutoff);
  }, []);

  const scheduleReconnect = useCallback((reconnect: () => Promise<void>) => {
    if (reconnectAttempts.current >= MAX_RECONNECT_ATTEMPTS) {
      return;
    }
    const delayMs = getRetryDelaySeconds(reconnectAttempts.current + 1, { baseMs: 1_000 }) * 1_000;
    reconnectAttempts.current += 1;
    reconnectTimer.current = setTimeout(() => {
      void reconnect();
    }, delayMs);
  }, []);

  const connect = useCallback(async () => {
    if (!pageId || pageId === "new") {
      return;
    }

    try {
      const session = await apiFetch<{ data: PageCollabSessionBridge }>(
        `/crm/pages/${pageId}/collaboration/session`,
        { method: "POST" },
      );
      if (!shouldReconnectRef.current) {
        return;
      }
      setCurrentUser(session.data.currentUser);
      setCollaborators(session.data.collaborators);
      setConnectError(null);

      const ws = new WebSocket(session.data.websocketUrl);
      wsRef.current = ws;

      ws.addEventListener("open", () => {
        setIsConnected(true);
        reconnectAttempts.current = 0;
        pendingDiffsRef.current = [];
      });

      ws.addEventListener("message", (event) => {
        const message = parseCollabMessage(event.data);
        if (!message) {
          return;
        }

        prunePendingDiffs();

        if (message.userId === currentUserRef.current?.userId) {
          return;
        }

        if (message.type === "user_joined") {
          const user = (message.payload as unknown as UserJoinedPayload).user;
          setCollaborators((prev) => mergeCollaborator(prev, user));
          return;
        }

        if (message.type === "user_left") {
          const payload = message.payload as unknown as UserLeftPayload;
          setCollaborators((prev) => prev.filter((entry) => entry.userId !== payload.userId));
          return;
        }

        if (message.type === "cursor_update") {
          const payload = message.payload as {
            cursor: CursorPosition;
            displayName: string;
            color: string;
          };
          setCollaborators((prev) => mergeCollaborator(prev, {
            userId: message.userId,
            displayName: payload.displayName,
            color: payload.color,
            role: prev.find((entry) => entry.userId === message.userId)?.role ?? "editor",
            cursor: payload.cursor,
            joinedAt: prev.find((entry) => entry.userId === message.userId)?.joinedAt ?? Date.now(),
            lastActiveAt: message.timestamp,
          }));
          return;
        }

        if (message.type === "edit_diff") {
          const payload = message.payload as unknown as EditDiffPayload;
          const incomingDiff = isPuckDiffOp(payload.diff) ? payload.diff : null;
          const conflict = incomingDiff
            ? pendingDiffsRef.current
              .map((entry) => entry.payload.diff)
              .filter(isPuckDiffOp)
              .map((diff) => detectConflict(incomingDiff, [diff]))
              .find((entry) => entry.hasConflict)
            : undefined;

          if (conflict?.hasConflict && conflict.conflictingComponentId) {
            onConflictRef.current?.({
              componentId: conflict.conflictingComponentId,
              editedBy: message.userId,
              editedByName: collaboratorsRef.current.find((entry) => entry.userId === message.userId)?.displayName
                ?? "Another editor",
              description: payload.description,
            });
          }

          onRemoteDiffRef.current?.({
            payload,
            userId: message.userId,
            pageId: message.pageId,
            timestamp: message.timestamp,
          });
          return;
        }

        if (message.type === "comment_added" || message.type === "comment_resolved") {
          const payload = message.payload as RemoteCommentEvent["payload"];
          onCommentEventRef.current?.({
            eventType: message.type === "comment_added" ? "added" : "resolved",
            payload,
            userId: message.userId,
            pageId: message.pageId,
            timestamp: message.timestamp,
          });
        }
      });

      ws.addEventListener("close", () => {
        setIsConnected(false);
        wsRef.current = null;
        if (shouldReconnectRef.current) {
          scheduleReconnect(connect);
        }
      });

      ws.addEventListener("error", () => {
        setConnectError("Realtime collaboration connection failed");
      });
    } catch (error) {
      setIsConnected(false);
      setConnectError(error instanceof Error ? error.message : "Realtime collaboration unavailable");
      scheduleReconnect(connect);
    }
  }, [pageId, prunePendingDiffs, scheduleReconnect]);

  const currentUserRef = useRef<PageCollabSessionBridge["currentUser"] | null>(null);
  const collaboratorsRef = useRef<CollabUser[]>([]);

  useEffect(() => {
    currentUserRef.current = currentUser;
  }, [currentUser]);

  useEffect(() => {
    collaboratorsRef.current = collaborators;
  }, [collaborators]);

  useEffect(() => {
    shouldReconnectRef.current = true;
    void connect();

    return () => {
      shouldReconnectRef.current = false;
      if (reconnectTimer.current) {
        clearTimeout(reconnectTimer.current);
      }
      if (cursorFlushTimerRef.current) {
        clearTimeout(cursorFlushTimerRef.current);
      }
      wsRef.current?.close();
      wsRef.current = null;
    };
  }, [connect]);

  const flushCursor = useCallback(() => {
    if (wsRef.current?.readyState !== WebSocket.OPEN || !pageId || !pendingCursorRef.current) {
      return;
    }

    wsRef.current.send(JSON.stringify({
      type: "page_presence",
      pageId,
      cursor: pendingCursorRef.current,
    }));
    lastCursorSentAtRef.current = Date.now();
    pendingCursorRef.current = null;
  }, [pageId]);

  const sendCursor = useCallback((cursor: CursorPosition) => {
    pendingCursorRef.current = cursor;

    const elapsed = Date.now() - lastCursorSentAtRef.current;
    if (elapsed >= CURSOR_THROTTLE_MS && !cursorFlushTimerRef.current) {
      flushCursor();
      return;
    }

    if (cursorFlushTimerRef.current) {
      return;
    }

    cursorFlushTimerRef.current = setTimeout(() => {
      cursorFlushTimerRef.current = null;
      flushCursor();
    }, Math.max(0, CURSOR_THROTTLE_MS - elapsed));
  }, [flushCursor]);

  const sendDiff = useCallback((payload: EditDiffPayload) => {
    if (wsRef.current?.readyState !== WebSocket.OPEN || !pageId) {
      return;
    }
    prunePendingDiffs();
    pendingDiffsRef.current.push({
      payload,
      createdAt: Date.now(),
    });
    wsRef.current.send(JSON.stringify({
      type: "page_edit_diff",
      pageId,
      diff: payload.diff,
      description: payload.description,
      affectedComponentIds: payload.affectedComponentIds,
    }));
  }, [pageId, prunePendingDiffs]);

  const sendCommentEvent = useCallback((
    payload: RemoteCommentEvent["payload"] & { eventType: RemoteCommentEvent["eventType"] },
  ) => {
    if (wsRef.current?.readyState !== WebSocket.OPEN || !pageId) {
      return;
    }

    wsRef.current.send(JSON.stringify({
      type: "page_comment_event",
      pageId,
      eventType: payload.eventType,
      commentId: payload.commentId,
      componentId: payload.componentId,
      componentType: payload.componentType,
      authorName: payload.authorName,
      resolvedBy: payload.resolvedBy ?? null,
    }));
  }, [pageId]);

  const dedupedCollaborators = useMemo(() => {
    const currentUserId = currentUser?.userId;
    const deduped = new Map<string, CollabUser>();
    for (const collaborator of collaborators) {
      deduped.set(collaborator.userId, collaborator);
    }
    if (currentUserId && !deduped.has(currentUserId)) {
      deduped.set(currentUserId, {
        userId: currentUser.userId,
        displayName: currentUser.displayName,
        color: currentUser.color,
        role: currentUser.role,
        joinedAt: Date.now(),
        lastActiveAt: Date.now(),
      });
    }
    return Array.from(deduped.values());
  }, [collaborators, currentUser]);

  return {
    collaborators: dedupedCollaborators,
    currentUser,
    isConnected,
    connectError,
    sendCursor,
    sendDiff,
    sendCommentEvent,
  };
}

function parseCollabMessage(raw: unknown): CollabMessage | null {
  try {
    const text = typeof raw === "string" ? raw : String(raw);
    const parsed = JSON.parse(text) as CollabMessage;
    if (!parsed?.type || !parsed.userId || !parsed.pageId) {
      return null;
    }
    return parsed;
  } catch {
    return null;
  }
}

function mergeCollaborator(
  current: CollabUser[],
  next: CollabUser,
): CollabUser[] {
  const index = current.findIndex((entry) => entry.userId === next.userId);
  if (index === -1) {
    return [...current, next];
  }
  const updated = [...current];
  updated[index] = {
    ...updated[index],
    ...next,
  };
  return updated;
}
